import {
  ElementRef,
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';

import {
  TheoryLevelResponse,
  TheoryLevelRequest,
  SkillDocumentResponse,
} from '../../../models/skill-learning.model';

const MIN_QUESTIONS_COUNT = 1;
const MAX_QUESTIONS_COUNT = 100;
const MIN_PASSING_PERCENTAGE = 1;
const MAX_PASSING_PERCENTAGE = 100;
const MIN_TIME_LIMIT_MINUTES = 1;
const MIN_TOPICS_COUNT = 10;
const SCROLLABLE_TOPICS_COUNT = 6;

type TheoryLevelInput = TheoryLevelResponse | null;
type SkillDocumentInput = SkillDocumentResponse | null;

export interface TheoryLevelSavePayload {
  request: TheoryLevelRequest;
  documentFile: File | null;
}

@Component({
  selector: 'app-skill-theory-level-editor',
  templateUrl: './theory-level-editor.component.html',
  styleUrls: ['./theory-level-editor.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SkillTheoryLevelEditorComponent implements OnInit, OnChanges {

  @Input() theoryLevel: TheoryLevelInput = null;
  @Input() documentFileId: string | null = null;
  @Input() document: SkillDocumentInput = null;
  @Input() isSubmitting: boolean = false;

  @Output() levelSaved = new EventEmitter<TheoryLevelSavePayload>();
  @Output() documentDownloadRequested = new EventEmitter<void>();

  @ViewChild('topicsList') topicsListRef: ElementRef;

  form: FormGroup;
  pendingDocument: File | null = null;

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.form = this.buildForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.theoryLevel && changes.theoryLevel.currentValue) {
      this.form = this.buildForm();
    }
  }

  get topicsArray(): FormArray {
    return this.form.get('topics') as FormArray;
  }

  get isFormInvalid(): boolean {
    return this.form.invalid || this.hasDuplicateTopics || this.isTooFewTopics || !this.hasRequiredDocument;
  }

  get hasRequiredDocument(): boolean {
    return this.hasPendingDocument || this.hasUploadedDocument;
  }

  get hasPendingDocument(): boolean {
    return !!this.pendingDocument;
  }

  get hasUploadedDocument(): boolean {
    if (this.hasDocumentMetadata()) {
      return true;
    }
    return this.hasDocumentIdentifier(this.documentFileId);
  }

  get isTooFewTopics(): boolean {
    return this.topicsArray.length < MIN_TOPICS_COUNT;
  }

  get shouldUseScrollableTopicList(): boolean {
    return this.topicsArray.length >= SCROLLABLE_TOPICS_COUNT;
  }

  get topicsCountMessageParams(): { count: number; minimum: number } {
    return {
      count: this.topicsArray.length,
      minimum: MIN_TOPICS_COUNT,
    };
  }

  get hasDuplicateTopics(): boolean {
    return this.collectDuplicateIndices().size > 0;
  }

  isTopicDuplicate(index: number): boolean {
    const duplicateIndices = this.collectDuplicateIndices();
    return duplicateIndices.has(index);
  }

  normalizeTopic(topic: string): string {
    return topic.trim().toLowerCase();
  }

  private collectDuplicateIndices(): Set<number> {
    const duplicateIndices = new Set<number>();
    const topicToIndices = this.buildTopicToIndicesMap();
    topicToIndices.forEach(indices => {
      if (indices.length > 1) {
        indices.forEach(i => duplicateIndices.add(i));
      }
    });
    return duplicateIndices;
  }

  private buildTopicToIndicesMap(): Map<string, number[]> {
    const topics = this.topicsArray.value as string[];
    const topicToIndices = new Map<string, number[]>();
    for (let i = 0; i < topics.length; i++) {
      const normalized = this.normalizeTopic(topics[i]);
      if (!normalized) continue;
      const existing = topicToIndices.get(normalized) || [];
      existing.push(i);
      topicToIndices.set(normalized, existing);
    }
    return topicToIndices;
  }

  addTopic(): void {
    this.topicsArray.push(this.fb.control('', Validators.required));
    this.scheduleTopicsScroll();
  }

  removeTopic(index: number): void {
    this.topicsArray.removeAt(index);
  }

  onDocumentSelected(file: File | null): void {
    this.pendingDocument = file;
  }

  onDocumentDownload(): void {
    this.documentDownloadRequested.emit();
  }

  onSave(): void {
    if (this.isFormInvalid) {
      return;
    }
    const request: TheoryLevelRequest = {
      topics: this.topicsArray.value as string[],
      questionsCount: this.form.get('questionsCount').value,
      timeLimitMinutes: this.form.get('timeLimitMinutes').value,
      passingPercentage: this.form.get('passingPercentage').value,
    };
    this.levelSaved.emit({
      request,
      documentFile: this.pendingDocument,
    });
    this.pendingDocument = null;
  }

  trackByIndex(index: number): number {
    return index;
  }

  private buildForm(): FormGroup {
    const topics = this.theoryLevel && this.theoryLevel.topics
      ? this.theoryLevel.topics.map(t => this.fb.control(t, Validators.required))
      : [];
    return this.fb.group({
      topics: this.fb.array(topics),
      questionsCount: [
        this.theoryLevel ? this.theoryLevel.questionsCount : MIN_QUESTIONS_COUNT,
        [Validators.required, Validators.min(MIN_QUESTIONS_COUNT), Validators.max(MAX_QUESTIONS_COUNT)],
      ],
      timeLimitMinutes: [
        this.theoryLevel ? this.theoryLevel.timeLimitMinutes : MIN_TIME_LIMIT_MINUTES,
        [Validators.required, Validators.min(MIN_TIME_LIMIT_MINUTES)],
      ],
      passingPercentage: [
        this.theoryLevel ? this.theoryLevel.passingPercentage : MIN_PASSING_PERCENTAGE,
        [Validators.required, Validators.min(MIN_PASSING_PERCENTAGE), Validators.max(MAX_PASSING_PERCENTAGE)],
      ],
    });
  }

  private scheduleTopicsScroll(): void {
    setTimeout(() => this.scrollTopicsToBottom());
  }

  private scrollTopicsToBottom(): void {
    const element = this.getTopicsListElement();
    if (!element) {
      return;
    }
    element.scrollTop = element.scrollHeight;
  }

  private getTopicsListElement(): HTMLElement | null {
    if (!this.topicsListRef) {
      return null;
    }
    return this.topicsListRef.nativeElement as HTMLElement;
  }

  private hasDocumentMetadata(): boolean {
    if (!this.document) {
      return false;
    }
    return this.hasDocumentIdentifier(this.document.fileId);
  }

  private hasDocumentIdentifier(fileId: string | null): boolean {
    if (!fileId) {
      return false;
    }
    return fileId.trim().length > 0;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/admin/theory-level-editor/theory-level-editor.component.ts